/**
 * Mega Menu interactions
 * ----------------------
 * Desktop: hover + focus open panels; click-outside + Escape close them.
 * Mobile : tap a mega-toggle to drill into a panel; tap the back button
 *          (or Escape) to return to the main menu.
 * Also sets --site-nav-height on <html> so the mobile drawer + panels
 * sit flush under the actual nav bar at any size.
 */
(function () {
    'use strict';

    // Wait until the DOM is parsed before binding anything. Lets this script
    // be safely loaded in <head> without needing `defer`.
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    function init() {

    var DESKTOP_MQ = window.matchMedia('(min-width: 992px)');
    function isDesktop() { return DESKTOP_MQ.matches; }

    var nav = document.getElementById('site-navigation');

    /* ---------- nav size custom properties ----------
     * Three vars, split by how often they change:
     *   --utility-height : announcement bar height (static). Drives the sticky
     *                      negative offset for #wrapper-navbar.
     *   --header-height  : full header height = announcement + main nav (static).
     *                      Slides the video hero up behind the whole header and
     *                      sizes it to the viewport.
     *   --site-nav-height: LIVE bottom edge of #main-nav (scroll-updated). Drives
     *                      the mobile drawer offset — #main-nav is sticky, so the
     *                      announcement scrolls off and this shrinks accordingly.
     */

    // Static measurements — don't change on scroll, so keep them out of the
    // scroll handler (avoids per-scroll reflow from offsetHeight reads).
    function measureBars() {
        var utility = document.querySelector('.navigation-utility');
        var wrapper = document.getElementById('wrapper-navbar');
        var utilityHeight = (utility && utility.offsetParent !== null) ? utility.offsetHeight : 0;
        document.documentElement.style.setProperty('--utility-height', utilityHeight + 'px');
        if (wrapper) {
            document.documentElement.style.setProperty('--header-height', wrapper.offsetHeight + 'px');
        }
    }

    // Live offset for the mobile drawer: current bottom of #main-nav. Correct
    // at the top of the page (announcement visible) and once scrolled
    // (announcement gone, nav pinned). Scroll is locked while the drawer is
    // open, so the value stays put once it's been opened.
    function updateNavOffset() {
        var bar = document.getElementById('main-nav');
        if (bar) {
            document.documentElement.style.setProperty('--site-nav-height', Math.max(0, bar.getBoundingClientRect().bottom) + 'px');
        }
    }

    measureBars();
    updateNavOffset();
    window.addEventListener('resize', function () { measureBars(); updateNavOffset(); });
    window.addEventListener('load', function () { measureBars(); updateNavOffset(); });
    window.addEventListener('scroll', updateNavOffset, { passive: true });
    // Re-measure after web fonts finish loading (nav height can shift)
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { measureBars(); updateNavOffset(); });
    }

    /* ---------- sticky-state class on the main navbar ----------
     * When #main-nav is position:sticky and the user scrolls past the
     * announcement bar, add .is-stuck so the nav can pick up a solid
     * background / inverted colors if needed. The announcement bar is
     * the trigger: once its bottom edge crosses the top of #main-nav,
     * the nav is effectively "stuck".
     */

    var mainNav = document.getElementById('main-nav');
    var utility = document.querySelector('.navigation-utility');

    function updateStuckState() {
        if (!mainNav) return;
        // If there's no announcement bar, the nav is always "stuck" at scroll>0
        if (!utility) {
            mainNav.classList.toggle('is-stuck', window.scrollY > 4);
            return;
        }
        var rect = utility.getBoundingClientRect();
        // Announcement fully scrolled past → stuck
        mainNav.classList.toggle('is-stuck', rect.bottom <= 0);
    }
    updateStuckState();
    window.addEventListener('scroll', updateStuckState, { passive: true });
    window.addEventListener('resize', updateStuckState);

    /* desktop: reveal on scroll up; stays hidden in scroll-locked sections (add data-nav-lock to opt in) */

    var LOCK_SELECTOR = '.pin-spacer, .stats-scroller, .panel-scroller, .stories-slider, .story-stage, .give-hero, .program-marquee--parallax, [data-nav-lock]';
    var REVEAL_AFTER_UP = 12;
    var HIDE_AFTER_DOWN = 2;
    var wrapperEl = document.getElementById('wrapper-navbar');
    var reveal = { fixed: false, shown: false, lastY: window.scrollY, up: 0, light: false, ticking: false };

    function revealThreshold() {
        var hero = null;
        var found = document.querySelectorAll('.alignfull, [class*="hero"]');
        for (var i = 0; i < found.length && !hero; i++) {
            if (found[i].tagName !== 'IMG' && !found[i].closest('#wrapper-navbar, #search-modal, footer')) hero = found[i];
        }
        var bottom = hero ? hero.getBoundingClientRect().bottom + window.scrollY : 0;
        return Math.min(Math.max(bottom, 480), window.innerHeight * 1.25);
    }

    function inLockedSection() {
        var els = document.querySelectorAll(LOCK_SELECTOR);
        var vh = window.innerHeight;
        for (var i = 0; i < els.length; i++) {
            var r = els[i].getBoundingClientRect();
            if (r.top <= 1 && r.bottom >= vh - 1) return true;
        }
        return false;
    }

    function engageReveal() {
        if (reveal.fixed) return;
        wrapperEl.style.minHeight = wrapperEl.offsetHeight + 'px';
        reveal.light = document.body.classList.contains('nav-scheme-light');
        document.body.classList.remove('nav-scheme-light');
        mainNav.classList.add('nav-reveal');
        reveal.fixed = true;
        reveal.shown = false;
    }

    function releaseReveal() {
        if (!reveal.fixed) return;
        mainNav.classList.remove('nav-reveal', 'is-revealed');
        wrapperEl.style.minHeight = '';
        if (reveal.light) document.body.classList.add('nav-scheme-light');
        reveal.fixed = false;
        reveal.shown = false;
    }

    function showReveal() {
        if (reveal.shown) return;
        mainNav.classList.add('is-revealed');
        reveal.shown = true;
    }

    function hideReveal() {
        if (!reveal.shown || mainNav.contains(document.activeElement)) return;
        mainNav.classList.remove('is-revealed');
        reveal.shown = false;
        closeAll();
    }

    function updateReveal() {
        reveal.ticking = false;
        if (!mainNav || !wrapperEl || !isDesktop()) { releaseReveal(); return; }

        var y = Math.max(0, window.scrollY);
        var dy = y - reveal.lastY;
        reveal.lastY = y;

        if (y <= wrapperEl.offsetHeight + 40) { releaseReveal(); return; }
        engageReveal();

        var allowed = y >= revealThreshold() && !inLockedSection() && !document.body.classList.contains('search-modal-open');
        if (!allowed) { reveal.up = 0; hideReveal(); return; }

        if (dy < 0) {
            reveal.up -= dy;
            if (reveal.up >= REVEAL_AFTER_UP) showReveal();
        } else if (dy >= HIDE_AFTER_DOWN) {
            reveal.up = 0;
            hideReveal();
        }
    }

    function queueReveal() {
        if (reveal.ticking) return;
        reveal.ticking = true;
        window.requestAnimationFrame(updateReveal);
    }

    if (mainNav && wrapperEl) {
        window.addEventListener('scroll', queueReveal, { passive: true });
        window.addEventListener('resize', queueReveal);
        window.addEventListener('load', queueReveal);
        mainNav.addEventListener('focusin', function () { if (reveal.fixed) showReveal(); });
        queueReveal();
    }

    /* ---------- panel open/close ---------- */

    function openItem(item) {
        var toggle = item.querySelector('.mega-toggle');
        var panel  = item.querySelector('.mega-panel');
        if (!toggle || !panel) return;

        // Close siblings on desktop (one panel at a time)
        if (isDesktop()) {
            var siblings = item.parentNode.querySelectorAll('.mega-item.is-open');
            siblings.forEach(function (sib) { if (sib !== item) closeItem(sib); });
        }

        item.classList.add('is-open');
        panel.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
    }

    function closeItem(item) {
        var toggle = item.querySelector('.mega-toggle');
        var panel  = item.querySelector('.mega-panel');
        if (!toggle || !panel) return;
        item.classList.remove('is-open');
        panel.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
    }

    function closeAll(scope) {
        (scope || document).querySelectorAll('.mega-item.is-open').forEach(closeItem);
    }

    /* ---------- toggle binding ---------- */

    document.querySelectorAll('.mega-item').forEach(function (item) {
        var toggle = item.querySelector('.mega-toggle');
        if (!toggle) return;

        toggle.addEventListener('click', function (e) {
            e.preventDefault();
            if (item.classList.contains('is-open')) {
                closeItem(item);
            } else {
                openItem(item);
            }
        });

        // Desktop hover — open on enter, close on leave. A short close delay
        // (hover-intent) bridges the dead gap between the navbar and the panel
        // (panel opens 1.5rem below): crossing the gap fires mouseleave then
        // mouseenter; the pending close is cancelled before it runs, so the
        // menu no longer flickers open/closed.
        var hoverTimer = null;
        item.addEventListener('mouseenter', function () {
            if (!isDesktop()) return;
            if (item._fhQuietUntil && Date.now() < item._fhQuietUntil) return;
            clearTimeout(hoverTimer);
            openItem(item);
        });
        item.addEventListener('mouseleave', function () {
            if (!isDesktop()) return;
            clearTimeout(hoverTimer);
            hoverTimer = setTimeout(function () { closeItem(item); }, 150);
        });

        // Keyboard
        item.addEventListener('focusin', function () {
            if (isDesktop()) openItem(item);
        });
        item.addEventListener('focusout', function (e) {
            if (!isDesktop()) return;
            if (!item.contains(e.relatedTarget)) closeItem(item);
        });
    });

    /* ---------- back button inside each panel (mobile drill-down) ----------
     * Using event delegation so this works even if .mega-panel-back is
     * rendered dynamically or nested inside other elements.
     */

    document.addEventListener('click', function (e) {
        var back = e.target.closest && e.target.closest('.mega-panel-back');
        if (!back) return;
        e.preventDefault();
        e.stopPropagation();
        var item = back.closest('.mega-item');
        if (item) closeItem(item);
    }, true); // capture phase — runs before the outside-click handler below

    /* panel links: close explicitly, same-page anchors don't reload */

    document.addEventListener('click', function (e) {
        var link = e.target.closest && e.target.closest('.mega-panel a[href]');
        if (!link) return;
        if (isDesktop()) {
            var item = link.closest('.mega-item');
            if (item) {
                item._fhQuietUntil = Date.now() + 800;
                closeItem(item);
            }
            return;
        }
        if (link.pathname === window.location.pathname && link.hash && nav && nav.classList.contains('show') && window.bootstrap) {
            var inst = window.bootstrap.Collapse.getInstance(nav) ||
                       new window.bootstrap.Collapse(nav, { toggle: false });
            inst.hide();
        }
    });

    /* ---------- outside click + Escape ---------- */

    document.addEventListener('click', function (e) {
        // Don't trigger on outside-click inside the mobile drawer panel
        if (!isDesktop()) return;
        if (!e.target.closest('.mega-item')) closeAll();
    });

    document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;

        // If any panel is open, close it first
        var anyOpen = document.querySelector('.mega-item.is-open');
        if (anyOpen) {
            closeAll();
            return;
        }
        // Otherwise ask Bootstrap to hide the drawer
        if (nav && nav.classList.contains('show') && window.bootstrap) {
            var inst = window.bootstrap.Collapse.getInstance(nav) ||
                       new window.bootstrap.Collapse(nav, { toggle: false });
            inst.hide();
        }
    });

    /* ---------- scroll-lock driven by Bootstrap collapse events ----------
     * Bootstrap owns the hamburger toggle (data-bs-toggle="collapse" on the
     * button). We listen for its show.bs.collapse / hide.bs.collapse events
     * on our #site-navigation element and mirror the state to body.
     */

    function setBodyScrollLock(locked) {
        document.body.classList.toggle('mega-nav-open', !!locked);
    }

    if (nav) {
        nav.addEventListener('shown.bs.collapse', function () {
            setBodyScrollLock(!isDesktop());
        });
        nav.addEventListener('hidden.bs.collapse', function () {
            setBodyScrollLock(false);
            closeAll(nav);
        });
    }

    /* ---------- reset state when crossing the breakpoint ---------- */

    DESKTOP_MQ.addEventListener('change', function () {
        closeAll();
        if (nav && nav.classList.contains('show') && window.bootstrap) {
            var inst = window.bootstrap.Collapse.getInstance(nav) ||
                       new window.bootstrap.Collapse(nav, { toggle: false });
            inst.hide();
        }
        setBodyScrollLock(false);
    });

    } // end init
})();